import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Open on the tap, not after the server answers. An empty cart shows the item being added meanwhile.
    const wasEmpty = this.#isCartEmpty();
    const openedEarly = shouldAutoOpen && !sourceModal;
    if (openedEarly) {
      this.#showPendingAdd(event, wasEmpty);
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        this.#clearPendingAdd();
        if (openedEarly && wasEmpty && detail?.didError) this.#themeDrawer?.close();

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#clearPendingAdd();
        if (openedEarly && wasEmpty) this.#themeDrawer?.close();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Shows the item being added while /cart/add.js is in flight.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {boolean} wasEmpty
   */
  #showPendingAdd(event, wasEmpty) {
    const dialog = this.#dialog;
    if (!dialog) return;
    this.#clearPendingAdd(0);
    dialog.classList.add('mw-cart-adding');
    if (!wasEmpty) return;

    const root = event.target instanceof Element ? event.target.closest('[data-valmonti-product]') : null;
    /** @type {any} */
    let variant = null;
    try {
      const variants = JSON.parse(root?.querySelector('[data-valmonti-variants]')?.textContent ?? '[]');
      const id = String(event.lines?.[0]?.merchandiseId ?? '').split('/').pop();
      variant = variants.find((/** @type {any} */ item) => String(item.id) === id) ?? null;
    } catch (_) {
      variant = null;
    }
    const el = (/** @type {string} */ tag, /** @type {string} */ className, text = '') => {
      const node = document.createElement(tag);
      node.className = className;
      if (text) node.textContent = text;
      return node;
    };

    const panel = el('div', 'mw-cart-pending');
    panel.setAttribute('aria-hidden', 'true');
    panel.append(el('p', 'mw-cart-pending__heading', 'Adding to your cart…'));
    const title = root?.querySelector('h1')?.textContent?.trim();
    if (title) {
      const row = el('div', 'mw-cart-pending__row');
      const variantSrc = variant?.featured_image?.src;
      const src = variantSrc
        ? `${variantSrc}${variantSrc.includes('?') ? '&' : '?'}width=200`
        : root?.querySelector('add-to-cart-component')?.dataset.productVariantMedia;
      if (src) {
        const image = /** @type {HTMLImageElement} */ (el('img', 'mw-cart-pending__image'));
        image.src = src;
        image.alt = '';
        row.append(image);
      }
      const text = el('div', 'mw-cart-pending__text');
      text.append(el('span', 'mw-cart-pending__title', title));
      if (variant?.title && variant.title !== 'Default Title') {
        text.append(el('span', 'mw-cart-pending__variant', variant.title));
      }
      const price = root?.querySelector('[data-valmonti-price]')?.textContent?.trim();
      if (price) text.append(el('span', 'mw-cart-pending__price', price));
      row.append(text);
      panel.append(row);
    }
    dialog.append(panel);
  }

  /** @param {number} [delay] - Lets the cart markup paint before the placeholder goes. */
  #clearPendingAdd(delay = 200) {
    const dialog = this.#dialog;
    if (!dialog) return;
    dialog.classList.remove('mw-cart-adding');
    const panels = dialog.querySelectorAll('.mw-cart-pending');
    if (delay) setTimeout(() => panels.forEach((panel) => panel.remove()), delay);
    else panels.forEach((panel) => panel.remove());
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
